import React, { createContext, useContext, useMemo } from "react";
import { globalEnv } from "~/global-env";
import {
  ComplianceCaseOrigin as ComplianceCaseOriginEnum,
  ComplianceCaseTypeEnum as ComplianceCaseTypeEnumEnum,
  ComplianceExternalObjectType,
} from "~/graphql/supergraphEnums";
import { useGetCaseArenaByProfileQuery } from "~/graphql/supergraphHooks";
import {
  GetComplianceCaseQuery,
  type ComplianceCaseTypeEnum,
  type Program,
} from "~/graphql/supergraphTypes";
import { toDbId } from "~/utils/graphql-helpers";
import {
  useComplianceAiExtraction,
  useDragAndDropProfilesFeatureFlag,
  useEnableClientLevelPermissions,
} from "../flags";
import { isIndividualProfile, isJointProfile } from "../utils/accountTypeUtils";
import { MAIN_SERVICE } from "../utils/constants";

type CaseProgram = Pick<Program, "id" | "name">;

const HIERARCHY_EDIT_CASE_TYPES: ReadonlyArray<ComplianceCaseTypeEnum> = [
  ComplianceCaseTypeEnumEnum.InvestorOnboarding,
  ComplianceCaseTypeEnumEnum.EntityOnboarding,
  ComplianceCaseTypeEnumEnum.ClientOnboarding,
];

export const isHierarchyEditCaseType = (caseType?: ComplianceCaseTypeEnum | null): boolean =>
  !!caseType && HIERARCHY_EDIT_CASE_TYPES.includes(caseType);

// Arena used for all cases before client-level permissions are enabled.
const DEFAULT_ARENA_ACCESS_ID = 1;

export const ARENA_UNRESOLVED_ERROR_MESSAGE =
  "Unable to determine the client workspace for this case. Please try again.";

type CurrentCaseContextValue = {
  caseId: number;
  caseType?: ComplianceCaseTypeEnum;
  aiExtractionSupported: boolean;
  arenaAccessId?: number;
  programId: number;
  caseBasePath: string;
  readonly: boolean;
  editHierarchySupported: boolean;
  investingProfileId?: number;
  isIndividualRootProfile: boolean;
  isJointRootProfile: boolean;
  casePrograms: CaseProgram[];
  // Per-entity FATCA/CRS config for the case's entity (COMP-4734). Consumers pass this to
  // useIsFatcaCrsEnabledForCase; under the migration flag it replaces the legacy flag reads.
  fatcaCrsEnabled: boolean;
};

const CurrentCaseContext = createContext<CurrentCaseContextValue | undefined>(undefined);

const parseArenaObjectId = (objectId?: string | null): number | undefined => {
  if (!objectId) return undefined;
  const parsed = parseInt(objectId, 10);
  if (!Number.isNaN(parsed)) return parsed;
  try {
    const dbId = toDbId(objectId);
    return Number.isNaN(dbId) ? undefined : dbId;
  } catch {
    return undefined;
  }
};

type Props = {
  complianceCase?: GetComplianceCaseQuery["complianceCase"] | null;
  caseId: number;
  children: React.ReactNode;
};

export const CurrentCaseContextProvider: React.FC<Props> = ({
  complianceCase,
  caseId,
  children,
}) => {
  const isAiExtractionEnabled = useComplianceAiExtraction();
  const isDragAndDropProfilesEnabled = useDragAndDropProfilesFeatureFlag();
  const clientLevelPermissionsEnabled = useEnableClientLevelPermissions();
  const externalArenaId = parseArenaObjectId(complianceCase?.externalArena?.objectId);

  // Standalone cases (e.g. document/profile refresh) carry no externalArena, so the
  // arena is resolved from the investing profile's onboarding case lineage instead.
  const investingProfileGuid = complianceCase?.investingProfile?.profileGuid;
  const { data: arenaByProfileData } = useGetCaseArenaByProfileQuery({
    variables: {
      externalObject: {
        objectId: investingProfileGuid ?? "",
        objectTypeEnum: ComplianceExternalObjectType.ComplianceProfile,
        service: MAIN_SERVICE,
      },
    },
    skip:
      !clientLevelPermissionsEnabled ||
      !complianceCase ||
      externalArenaId !== undefined ||
      !investingProfileGuid,
  });
  const arenaAccessId = clientLevelPermissionsEnabled
    ? (externalArenaId ??
      arenaByProfileData?.complianceCaseArenaByProfile?.arenaId ??
      undefined)
    : DEFAULT_ARENA_ACCESS_ID;

  const contextValue = useMemo(
    (): CurrentCaseContextValue => ({
      caseId: caseId,
      caseType: complianceCase?.caseType,
      aiExtractionSupported:
        complianceCase?.caseType === ComplianceCaseTypeEnumEnum.InvestorOnboarding &&
        complianceCase?.origin === ComplianceCaseOriginEnum.Manual &&
        isAiExtractionEnabled,
      arenaAccessId,
      editHierarchySupported:
        isHierarchyEditCaseType(complianceCase?.caseType) &&
        complianceCase?.origin === ComplianceCaseOriginEnum.Manual &&
        isDragAndDropProfilesEnabled,
      programId: complianceCase?.programId ?? 0,
      caseBasePath: `${globalEnv.complianceRoute}/case/${caseId}`,
      readonly: complianceCase?.readonly ?? true,
      investingProfileId: complianceCase?.investingProfile?.id,
      isIndividualRootProfile: isIndividualProfile(
        complianceCase?.investingProfile?.accountType,
        complianceCase?.investingProfile?.complianceObjectType,
      ),
      isJointRootProfile: isJointProfile(
        complianceCase?.investingProfile?.accountType,
        complianceCase?.investingProfile?.complianceObjectType,
      ),
      casePrograms: complianceCase?.casePrograms ?? [],
      fatcaCrsEnabled: complianceCase?.entity?.complianceSettings?.fatcaCrsEnabled ?? false,
    }),
    [
      caseId,
      arenaAccessId,
      complianceCase?.caseType,
      complianceCase?.origin,
      complianceCase?.programId,
      complianceCase?.readonly,
      complianceCase?.investingProfile?.id,
      complianceCase?.investingProfile?.accountType,
      complianceCase?.investingProfile?.complianceObjectType,
      complianceCase?.casePrograms,
      complianceCase?.entity?.complianceSettings?.fatcaCrsEnabled,
      isAiExtractionEnabled,
      isDragAndDropProfilesEnabled,
    ],
  );

  return (
    <CurrentCaseContext.Provider value={contextValue}>{children}</CurrentCaseContext.Provider>
  );
};

export const useCurrentCaseContext = (): CurrentCaseContextValue => {
  const context = useContext(CurrentCaseContext);
  if (!context) {
    throw new Error("useCurrentCaseContext must be used within a CurrentCaseContextProvider");
  }
  return context;
};
